import { Tabs as TabsPrimitive } from 'radix-ui';
import * as React from 'react';
import { memo } from 'react';

import { createSafeContext } from '@/hooks/createContextStore.hooks';
import { cn } from '@/lib/utils';

type TabsSize = 'default' | 'lg';

const [TabsContextProvider, useTabsContext] = createSafeContext<{
  size: TabsSize;
}>('Tabs Context not found');

function Tabs({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & { size?: TabsSize }) {
  return (
    <TabsContextProvider value={{ size }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn('flex flex-col gap-2', className)}
        {...props}
      />
    </TabsContextProvider>
  );
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { size } = useTabsContext();

  return (
    <TabsPrimitive.List
      ref={ref}
      data-slot="tabs-list"
      className={cn(
        'bg-muted text-muted-foreground inline-flex gap-1',
        'w-fit items-center justify-center rounded-lg p-[3px] border border-border-strong',
        size === 'lg' ? 'h-10' : 'h-9',
        className
      )}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { size } = useTabsContext();

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-slot="tabs-trigger"
      className={cn(
        'text-xs aria-[selected=false]:hover:bg-secondary-hover',
        'aria-selected:bg-secondary-active aria-selected:shadow-sm inline-flex',
        'h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5',
        'rounded-md border border-transparent px-2 py-1',
        'whitespace-nowrap transition-[color,background-color,border-color,transform]',
        'duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]',
        'motion-safe:active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none',
        "[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'focus-ring',
        size === 'lg' && 'py-3',
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = memo(
  React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
  >(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
      ref={ref}
      data-slot="tabs-content"
      className={cn('flex-1 outline-none mt-2', className)}
      {...props}
    />
  ))
);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
